import { useCallback, useEffect, useState } from 'react'
import { useMutation } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'

import { exchangeInstallationCode as exchangeInstallationCodeDescriptor } from '@jotta/grpc-connect-all/loginQuery'
import { useGetConfigSuspense } from '@jotta/grpc-connect-client/config'
import { publicTransport } from '@jotta/grpc-connect-client/transport'
import { mutate } from '@jotta/grpc-connect-client/util'
import type { Credentials } from '@jotta/grpc-connect-openapi/credentials'
import { Challenge } from '@jotta/grpc-connect-openapi/credentials'
import { Feature } from '@jotta/grpc-connect-openapi/feature'
import type { EmailLoginResponse } from '@jotta/grpc-connect-openapi/login'
import {
  EmailLoginRequestSchema,
  ExchangeInstallationCodeRequestSchema,
  FederatedLoginRequestSchema,
} from '@jotta/grpc-connect-openapi/login'
import {
  emailLogin as emailLoginDescriptor,
  federatedLogin as federatedLoginDescriptor,
} from '@jotta/grpc-connect-openapi/loginQuery'
import { type Form as UseFormResult, useForm } from '@jotta/hooks'
import type { PartialMessage } from '@jotta/types/compat'
import { ChallengeDialogModal } from '@jotta/ui/ChallengeDialog'
import { OrDivider } from '@jotta/ui/Divider'
import { ErrorMessage } from '@jotta/ui/ErrorMessage'
import { Form } from '@jotta/ui/Form'
import { FormField } from '@jotta/ui/FormField'
import { FormFieldErrorMessage } from '@jotta/ui/Label'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { PasswordInput } from '@jotta/ui/PasswordInput'
import { Redirect } from '@jotta/ui/Redirect'
import Spinner from '@jotta/ui/Spinner'
import { TextInput } from '@jotta/ui/TextInput'
import { autoLogin } from '@jotta/utils/auth'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { redirectAndWait } from '@jotta/utils/redirect'

import { CreateUser } from './CreateUser'
import { CTAButton } from './CTAButton'
import { Federation } from './Federation'
import {
  getInstallationCode,
  persistInstallationCode,
} from './installationCodeStore'
import { LoginExpired } from './LoginExpired'
import { PreSignup } from './PreSignup'
import { ResetPassword } from './ResetPassword'
import { generateSignupToken } from './signupForm'
import { useFederationStore } from './useFederationStore'

export const restartLoginUrl = '/api/login?redirect_uri=/'

function LoginForm({
  form,
  federatedLoginError,
  showLinkedAccounts,
  wrongPassword,
  loginFailed,
  onSignupRequested,
}: {
  form: UseFormResult<any>
  federatedLoginError: Error | null
  showLinkedAccounts: boolean | undefined
  wrongPassword?: boolean
  loginFailed?: boolean
  onSignupRequested?: () => void
}) {
  const [forgotPassword, setForgotPassword] = useState(false)
  const { email, password } = form

  return (
    <>
      <ModalDialog
        open={forgotPassword}
        className="!px-8 !py-16"
        onClose={() => setForgotPassword(false)}
      >
        <ResetPassword onClose={() => setForgotPassword(false)} />
      </ModalDialog>

      <h1 className="body-lg-ln pb-12">
        <Trans>Log In</Trans>
      </h1>

      {showLinkedAccounts && (
        <>
          <Federation
            intent={{
              case: 'login',
              value: {},
            }}
          />
          <OrDivider />
        </>
      )}

      <ErrorMessage error={federatedLoginError} />

      <Form form={form} className="flex flex-col gap-4">
        {loginFailed && (
          <div className="alert alert-danger p-6">
            <header className="alert-title">
              <Trans>We couldn’t sign you in</Trans>
            </header>
            <div>
              <Trans>
                Either your email or password was incorrect. It might be a small
                typo or time for a{' '}
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={event => {
                    event.preventDefault()
                    setForgotPassword(true)
                  }}
                >
                  password reset
                </button>
                .
              </Trans>
            </div>
          </div>
        )}

        <FormField field={email} label={t`Email`}>
          <TextInput {...email.inputProps} />
        </FormField>

        <FormField field={password} label={t`Password`}>
          <PasswordInput {...password.inputProps} />
          <button
            type="button"
            className="btn btn-link self-start pt-2 text-signup-link"
            onClick={event => {
              event.preventDefault()
              setForgotPassword(true)
            }}
          >
            <Trans>Forgot password?</Trans>
          </button>
          {wrongPassword && (
            <FormFieldErrorMessage errorMessage={t`Invalid password`} />
          )}
        </FormField>

        <CTAButton type="submit">
          <Trans>Login</Trans>
        </CTAButton>

        {onSignupRequested && (
          <p className="text-grey4">
            <Trans>Don't have an account?</Trans>
            &nbsp;
            <button
              className="!text-signup-link underline"
              onClick={event => {
                event.preventDefault()
                onSignupRequested()
              }}
            >
              <Trans>Sign up now</Trans>
            </button>
          </p>
        )}
      </Form>
    </>
  )
}

const installationCode = getInstallationCode()

export function LoginPage({ loginToken }: { loginToken: string }) {
  const {
    data: { brandingInfo, activeFeatures, viewConfig },
  } = useGetConfigSuspense()
  const { pendingFederation } = useFederationStore()

  const brandCode = brandingInfo?.brandCode
  const showLinkedAccounts = viewConfig?.showLinkedAccounts

  const [signup, setSignup] = useState(false)
  const [emailLoginResponse, setEmailLoginResponse] =
    useState<EmailLoginResponse>()

  const { isFetching: isExchangingInstallationCode } = useQuery({
    enabled: Boolean(installationCode),
    queryKey: ['exchangeInstallationCode'],
    queryFn: async () => {
      persistInstallationCode(undefined)

      const result = (
        await mutate(
          exchangeInstallationCodeDescriptor,
          await generateSignupToken(
            ExchangeInstallationCodeRequestSchema,
            {
              brandCode,
              installationCode,
              loginToken,
            },
            (req, token) => (req.token = token),
          ),
          publicTransport,
        )
      ).result

      switch (result.case) {
        case 'installation': {
          const loggedIn = result.value.loggedIn
          if (loggedIn) {
            await autoLogin({
              username: loggedIn.username,
              token: loggedIn.autoLoginCode,
              redirectTo: '/web/sync',
            })
          }
          break
        }
        case 'redirectUrl':
          await redirectAndWait(result.value)
          break
        case undefined:
        case 'notFound':
          break
        default:
          return exhaustiveGuard(result)
      }

      return result
    },
  })

  const { mutate: federatedLogin, error: federatedLoginError } = useMutation(
    federatedLoginDescriptor,
    { throwOnError: false },
  )

  const { mutateAsync: emailLogin, reset } = useMutation(emailLoginDescriptor, {
    onSuccess: setEmailLoginResponse,
    throwOnError: false,
  })

  const submitCredentials = useCallback(
    async (
      email: string | undefined,
      credentials: PartialMessage<Credentials>,
    ) =>
      emailLogin(
        await generateSignupToken(
          EmailLoginRequestSchema,
          {
            brandCode,
            loginToken,
            email,
            credentials,
          },
          (message, token) => (message.token = token),
        ),
      ),
    [emailLogin, brandCode, loginToken],
  )

  const form = useForm({
    fields: {
      email: {
        type: 'email',
      },
      password: {
        type: 'text',
      },
    },
    submit: async ({ email, password }) => {
      return submitCredentials(email, {
        password,
      })
    },
  })

  useEffect(() => {
    if (pendingFederation && !federatedLoginError) {
      const { token, nonce } = pendingFederation
      generateSignupToken(
        FederatedLoginRequestSchema,
        {
          brandCode,
          loginToken,
          federationToken: token,
          nonce,
        },
        (message, token) => (message.token = token),
      ).then(value => federatedLogin(value))
    }
  }, [pendingFederation])

  if (
    (pendingFederation && !federatedLoginError) ||
    isExchangingInstallationCode
  ) {
    return <Spinner className="mx-auto" />
  }

  if (activeFeatures.includes(Feature.PRE_SIGNUP)) {
    return <PreSignup loginToken={loginToken} />
  }

  const signupSupported = activeFeatures.includes(Feature.SIGNUP)
  const voucherSignupSupported = activeFeatures.includes(Feature.VOUCHER_SIGNUP)

  if (signup) {
    return (
      <CreateUser
        allowAppPromotion
        type={signupSupported ? 'private' : 'voucher'}
        loginToken={loginToken}
        onClose={() => setSignup(false)}
        onRequestLogin={() => setSignup(false)}
      />
    )
  }

  const onSignupRequested =
    signupSupported || voucherSignupSupported
      ? () => setSignup(true)
      : undefined

  const result = emailLoginResponse?.result
  switch (result?.case) {
    case undefined:
      return (
        <LoginForm
          form={form}
          showLinkedAccounts={showLinkedAccounts}
          federatedLoginError={federatedLoginError}
          onSignupRequested={onSignupRequested}
        />
      )
    case 'loginFailed':
      return (
        <LoginForm
          form={form}
          showLinkedAccounts={showLinkedAccounts}
          federatedLoginError={federatedLoginError}
          onSignupRequested={onSignupRequested}
          loginFailed
        />
      )
    case 'challenges': {
      const challenges = result.value
      return challenges.challenges.includes(Challenge.PASSWORD) ? (
        <LoginForm
          form={form}
          showLinkedAccounts={showLinkedAccounts}
          federatedLoginError={federatedLoginError}
          onSignupRequested={onSignupRequested}
          wrongPassword
        />
      ) : (
        <ChallengeDialogModal
          requestType="positive"
          challenges={challenges}
          onRequest={credentials =>
            submitCredentials(form.email.value, credentials)
          }
          onClose={reset}
          defaultCredentials={{
            password: form.password.value,
          }}
        />
      )
    }
    case 'redirectUrl':
      return <Redirect to={result.value} reload />
    case 'loginExpired':
      return <LoginExpired />
    default:
      return exhaustiveGuard(result)
  }
}
